import {callSettingsDefaultValues} from '../components/Forms/AutoAttendants/CallSettings/CallSettingsForm.utils';
import {useIncomingAAFormTab} from './useIncomingAAFormTab';
import {useMemo} from 'react';
import {getServiceFeatureLockedStatus, getServiceFeatureValue,} from '../utils/extensions/getServiceValue';
import {ServiceFeatureName} from '../store/types/ServiceFeature';
import {useSelector} from 'react-redux';
import {ReduxState} from '../store/types';
import {ExtensionType} from "../store/types/Extension";

export const useAutoAttendantCallSettingsFormData = () => {
    const incomingInitData = useIncomingAAFormTab();

    const {
        serviceFeatures, callBarringRules, mohDetails, voicemailSettings,
    } = useSelector(
        (state: ReduxState) => state.extensions,
    );

    const {incomingTab} = useSelector(
        (state: ReduxState) => state.autoAttendants
    );

    const accountInfo = useSelector<ReduxState, ExtensionType | undefined>(state => state.autoAttendants?.autoAttendantInfo?.account_info);

    const autoAttendantServiceFeatures = useSelector(
        (state: ReduxState) => state.autoAttendants.autoAttendantDetails?.extension?.account_info?.service_features,
    );

    // Backend silently ignores DISA settings in set_vm_settings when the
    // auto_attendant service feature is disabled for the account
    const isDisaAvailable = useMemo(() => getServiceFeatureValue(
        ServiceFeatureName.AutoAttendant,
        autoAttendantServiceFeatures ?? serviceFeatures,
    ) !== 'N', [autoAttendantServiceFeatures, serviceFeatures]);

    const initialValues = useMemo(() => {
        return {
            ...callSettingsDefaultValues,
            ...incomingInitData.initValues,
            //Call barrings
            callBarringStatus:
                getServiceFeatureValue(
                    ServiceFeatureName.CallBarring,
                    serviceFeatures,
                ) === 'Y',
            callBarringLocked: getServiceFeatureLockedStatus(
                ServiceFeatureName.CallBarring,
                serviceFeatures,
            ),
            callBarringItems: callBarringRules || [],
            individualRules: false,

            //Call recordings
            callRecordingStatus:
                getServiceFeatureValue(
                    ServiceFeatureName.CallRecording,
                    serviceFeatures,
                ) === 'Y',
            callRecordingBlocked: getServiceFeatureLockedStatus(
                ServiceFeatureName.CallRecording,
                serviceFeatures,
            ),
            outgoingStatus:
                getServiceFeatureValue(
                    ServiceFeatureName.CallRecording,
                    serviceFeatures,
                    'call_recording_out',
                ) === '1',
            incomingStatus:
                getServiceFeatureValue(
                    ServiceFeatureName.CallRecording,
                    serviceFeatures,
                    'call_recording_in',
                ) === '2',
            redirectedStatus:
                getServiceFeatureValue(
                    ServiceFeatureName.CallRecording,
                    serviceFeatures,
                    'call_recording_redirect',
                ) === '4',
            playCallRecordingAnnouncementToAllStatus:
                getServiceFeatureValue(
                    ServiceFeatureName.CallRecording,
                    serviceFeatures,
                    'audible_notification',
                ) === 'Y',
            sendTheCallRecordingViaEmailStatus:
                getServiceFeatureValue(
                    ServiceFeatureName.CallRecording,
                    serviceFeatures,
                    'mail_recording',
                ) === 'Y',
            allowToStartStopRecordingManually:
                getServiceFeatureValue(
                    ServiceFeatureName.CallRecording,
                    serviceFeatures,
                    'on_demand',
                ) === 'Y',
            // Auto attendant details populate incomingTab; the account realm
            // (My extension) only has the account's own voicemail settings
            DISAPassword: incomingTab
                ? incomingTab.DISAPassword || ''
                : voicemailSettings?.disa_password || '',
            DISASwitcher: incomingTab
                ? incomingTab.enableDisa === 'Y'
                : voicemailSettings?.enable_disa === 'yes',
            onHoldMusicStatus: mohDetails?.isEnabled || false,
            onHoldMusicName:
                mohDetails?.items?.find(
                    (v) => v.i_moh === mohDetails?.selectedValueId,
                )?.name ?? '',

                
            //Prefered IVR Language
            preferredIvrLanguageStatus:
                getServiceFeatureValue(
                    ServiceFeatureName.PreferredIvrLanguage,
                    serviceFeatures,
                ) === 'Y',
            preferredIvrLanguageBlocked: getServiceFeatureLockedStatus(
                ServiceFeatureName.PreferredIvrLanguage,
                serviceFeatures,
            ),
            preferredIvrLanguageCode:
                getServiceFeatureValue(
                    ServiceFeatureName.PreferredIvrLanguage,
                    serviceFeatures,
                    'iso_639_1',
                ),
        };
    }, [incomingInitData, serviceFeatures, callBarringRules, incomingTab, mohDetails, voicemailSettings]);

    return {
        initialValues,
        data: {
            email: accountInfo?.email,
            isDisaAvailable,
            mohItems:
                mohDetails?.items.map((v) => ({
                    status: '',
                    id: v.i_moh || 0,
                    name: v.name,
                })) || [],
        }
    }
};
